import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createPatientClinicalForm,
  getPatientById,
  getPatientClinicalForm,
  updatePatientClinicalForm,
} from "../../../../services/patientService";
import { CalendarIcon, FormsIcon } from "../../../../Icons";
import {
  displayDateUS,
  formatDateForInput,
  toIsoDate,
} from "../../../../utils/dateUtils";
import {
  SAMPLE_CLINICAL_FORM_DATA,
  CLINICAL_FORM_FIELD_LABELS,
} from "../../clinicalFormCatalog";
import { getDedicatedFormPage } from "./dedicatedForms";
import {
  NEW_FORM_ID,
  clinicalFormCreatePayload,
  readClinicalFormDraft,
} from "./clinicalFormDraft";
import { useClinicalCosignPermissions } from "./hooks/useClinicalCosignPermissions";
import { resolveFormActorRole } from "./disciplineFormAccess";
import {
  FORM_FLAG_MESSAGES,
  Field,
  FormActionBar,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  HeaderCell,
  LockedReasonBanner,
  OfficeReviewSection,
  SHEET,
  Section,
  fieldClass,
  textareaClass,
} from "./formLayout";
import { PATIENT_PAGE_SHELL } from "../../patientPageLayout";
import usePatientTabs from "../../usePatientTabs";
import { withSearchParams } from "../../../../hooks/useUrlTab";
import DateInput from "../../../../components/datePicker/DateInput";

const humanizeKey = (key) =>
  key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());

const fieldLabel = (key) => CLINICAL_FORM_FIELD_LABELS[key] || humanizeKey(key);

const isDateField = (key) => key.toLowerCase().endsWith("date");
const isTextareaField = (key) =>
  !isDateField(key) && key !== "clinicianSignature";

/**
 * Generic Eval & Orders form page — same sheet chrome as NOMNC / Consent:
 * side margins, centered white card, shared header + FormActionBar.
 */
const PatientClinicalFormPage = () => {
  const { patientId, formId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isTabEnabled, loading: tabsLoading } = usePatientTabs();

  const [patient, setPatient] = useState(null);
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [reviewNotes, setReviewNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const role = resolveFormActorRole(currentUser);
  const actorUserId = currentUser?.id ? Number(currentUser.id) : null;

  const isDraft = formId === NEW_FORM_ID;
  const draft = useMemo(
    () => (isDraft ? readClinicalFormDraft(location.search) : null),
    [isDraft, location.search],
  );

  const backToPatient = useCallback(
    (tab) => {
      const returnTo = location.state?.returnTo;
      const nextTab = tab || location.state?.tab;
      const evalSection = location.state?.evalSection || null;
      if (returnTo) {
        const nextPath = nextTab
          ? withSearchParams(returnTo, {
              tab: nextTab,
              section: nextTab === "eval" && evalSection ? evalSection : null,
            })
          : returnTo;
        navigate(nextPath, {
          state: nextTab
            ? {
                tab: nextTab,
                ...(nextTab === "eval" && evalSection ? { evalSection } : {}),
              }
            : undefined,
        });
        return;
      }
      navigate(`/patients/${patientId}/charts`);
    },
    [location.state, navigate, patientId],
  );

  const handleDedicatedBack = useCallback(
    () => backToPatient(location.state?.tab || "eval"),
    [backToPatient, location.state?.tab],
  );

  const handleDedicatedCreated = useCallback(
    (newId) => {
      if (!newId) return;
      navigate(`/patients/${patientId}/forms/${newId}`, {
        replace: true,
        state: location.state,
      });
    },
    [navigate, patientId, location.state],
  );

  const isDailyLogsForm =
    draft?.formSection === "daily-logs" ||
    draft?.formType === "daily_log_form" ||
    form?.formSection === "daily-logs" ||
    form?.formType === "daily_log_form" ||
    location.state?.tab === "documents";
  const requiredTabOk = isDailyLogsForm
    ? isTabEnabled("documents")
    : isTabEnabled("eval");

  useEffect(() => {
    if (tabsLoading) return;

    // Wait until we know which form this is before gating existing rows.
    if (!isDraft && !form) return;

    if (isDailyLogsForm) {
      if (!isTabEnabled("documents")) {
        toast.error("Documents is not enabled for this agency");
        backToPatient("documents");
      }
      return;
    }

    if (!isTabEnabled("eval")) {
      toast.error("Eval & Orders is not enabled for this agency");
      backToPatient(location.state?.tab === "auth" ? "auth" : undefined);
    }
  }, [
    tabsLoading,
    isTabEnabled,
    backToPatient,
    location.state?.tab,
    isDraft,
    isDailyLogsForm,
    form,
  ]);

  const load = useCallback(async () => {
    if (tabsLoading) return;
    if (isDraft) {
      if (!draft) {
        toast.error("That form is no longer available");
        backToPatient();
        return;
      }
      const draftIsDaily =
        draft.formSection === "daily-logs" ||
        draft.formType === "daily_log_form";
      if (draftIsDaily && !isTabEnabled("documents")) return;
      if (!draftIsDaily && !isTabEnabled("eval")) return;
    }
    setLoading(true);
    try {
      const [patientRes, formRes] = await Promise.all([
        getPatientById(patientId),
        isDraft ? null : getPatientClinicalForm(patientId, formId),
      ]);
      const patientData = patientRes.result || patientRes.data || patientRes;
      const formData = isDraft
        ? {
            formType: draft.formType,
            formName: draft.formName,
            formSection: draft.formSection,
            status: "pending",
            scheduledVisit: draft.visit?.visitDate
              ? { visitDate: draft.visit.visitDate }
              : null,
          }
        : formRes.result?.form || formRes.form;

      if (
        (formData?.formSection === "daily-logs" ||
          formData?.formType === "daily_log_form") &&
        !isTabEnabled("documents")
      ) {
        toast.error("Documents is not enabled for this agency");
        backToPatient("documents");
        return;
      }

      if (
        formData?.formSection !== "daily-logs" &&
        formData?.formType !== "daily_log_form" &&
        !isDraft &&
        !isTabEnabled("eval")
      ) {
        toast.error("Eval & Orders is not enabled for this agency");
        backToPatient();
        return;
      }

      setPatient(patientData.patient || patientData);
      setForm(formData);
      setValues({
        ...SAMPLE_CLINICAL_FORM_DATA,
        ...(formData?.formData || {}),
      });
      setReviewNotes(formData?.reviewNotes || "");
    } catch (err) {
      toast.error(err.message || "Failed to load form");
      backToPatient();
    } finally {
      setLoading(false);
    }
  }, [
    patientId,
    formId,
    backToPatient,
    isDraft,
    draft,
    tabsLoading,
    isTabEnabled,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const { permissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId,
    formFilledByUserId: form?.filledBy || actorUserId,
    formFilledByRole: form?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const canEdit = Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);

  if (tabsLoading || loading) return <FormLoadingSkeleton />;
  if (!requiredTabOk) {
    return (
      <div className={`${PATIENT_PAGE_SHELL} text-sm text-slate-500`}>
        {isDailyLogsForm
          ? "Documents is not enabled for this agency."
          : "Eval & Orders is not enabled for this agency."}
      </div>
    );
  }
  if (!form || !patient) return null;

  const DedicatedForm = getDedicatedFormPage(form.formType);
  if (DedicatedForm) {
    const saved =
      form.formData && typeof form.formData === "object" ? form.formData : {};
    const linkedDate =
      saved.visitDate ||
      saved.evalDate ||
      form.scheduleDate ||
      form.scheduledVisit?.visitDate ||
      form.visitDate ||
      null;
    return (
      <DedicatedForm
        patient={patient}
        clinicalForm={{
          ...form,
          patientId: Number(patientId) || form.patientId,
          id: isDraft ? null : form.id,
          visitDate: saved.visitDate || linkedDate,
          evalDate: saved.evalDate || linkedDate,
        }}
        draft={isDraft ? draft : null}
        onBack={handleDedicatedBack}
        onCreated={handleDedicatedCreated}
      />
    );
  }

  const setFieldValue = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !reviewNotes.trim()) {
      toast.error("Review notes are required when marking To Be Corrected");
      return;
    }

    setSaving(flag);
    try {
      if (isDraft) {
        const res = await createPatientClinicalForm(
          patientId,
          clinicalFormCreatePayload(draft, {
            formData: values,
            flag,
          }),
        );
        const created = res.result?.form || res.form;
        if (!created?.id) throw new Error("Form was not created");
        toast.success(
          created?.scheduledVisit
            ? `${FORM_FLAG_MESSAGES[flag] || "Form saved"} and the visit is on the schedule`
            : FORM_FLAG_MESSAGES[flag] || "Form saved",
        );
        navigate(`/patients/${patientId}/forms/${created.id}`, {
          replace: true,
          state: location.state,
        });
        return;
      }

      const payload = {
        formData: values,
        flag,
        ...(isReviewer ? { reviewNotes: reviewNotes.trim() || null } : {}),
      };
      const res = await updatePatientClinicalForm(patientId, formId, payload);
      const updated = res.result?.form || res.form;
      setForm(updated);
      setValues({
        ...SAMPLE_CLINICAL_FORM_DATA,
        ...(updated?.formData || {}),
      });
      setReviewNotes(updated?.reviewNotes || "");
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
    } catch (err) {
      toast.error(
        err.message ||
          (isDraft ? "Failed to save form" : "Failed to update form"),
      );
    } finally {
      setSaving(null);
    }
  };

  const patientName = [patient.lastName, patient.firstName]
    .filter(Boolean)
    .join(", ");
  const formFieldKeys = Object.keys(values);
  const backTab = "eval";

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {/* Page chrome — outside the printed sheet (NOMNC pattern) */}
      <FormPageHeader
        patientName={patientName}
        mrn={patient.mrn}
        status={isDraft ? "pending" : form.status}
        onBack={() => backToPatient(backTab)}
      />

      {/* Single white sheet with side margins */}
      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            {form.formName}
          </h1>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <section className="rounded-md border border-slate-800 overflow-hidden">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-2.5 gap-y-2 px-3 py-1.5">
            <HeaderCell label="Patient Name" value={patientName} dense />
            <HeaderCell label="MRN" value={patient.mrn} dense />
            <HeaderCell
              label="Date of Birth"
              value={displayDateUS(patient.dateOfBirth)}
              dense
            />
            <HeaderCell label="Gender" value={patient.gender} dense />
          </div>
        </section>

        {isDraft ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-400">
            <span className="inline-flex items-center gap-1">
              <CalendarIcon size={11} /> Scheduled for{" "}
              {displayDateUS(form.scheduledVisit?.visitDate) || "today"}
            </span>
          </div>
        ) : (
          <FormMetaRow form={form} />
        )}

        {isDraft ? (
          <p className="flex items-start gap-2 px-3 py-1.5 text-[11px] text-indigo-800 bg-indigo-50 border border-indigo-100 rounded-md">
            Nothing is saved yet. Save
            {permissions.canToBeSign
              ? " or To Be Sign"
              : permissions.canSendToOffice
                ? " or Sent to Office"
                : ""}{" "}
            creates this form
            {form.scheduledVisit ? " and its scheduled visit" : ""}; leaving now
            discards it.
          </p>
        ) : null}

        <LockedReasonBanner reason={permissions.lockedReason} />

        <Section icon={FormsIcon} title="Form Details">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2.5">
            {formFieldKeys
              .filter(
                (key) =>
                  !(
                    permissions.hideClinicalSignature &&
                    key === "clinicianSignature"
                  ),
              )
              .map((key) => {
                const value = values[key] ?? "";
                if (isDateField(key)) {
                  return (
                    <Field key={key} label={fieldLabel(key)}>
                      <DateInput
                        className={fieldClass}
                        value={formatDateForInput(value) || ""}
                        disabled={!canEdit}
                        onChange={(e) =>
                          setFieldValue(
                            key,
                            e.target.value ? toIsoDate(e.target.value) : null,
                          )
                        }
                      />
                    </Field>
                  );
                }
                if (!isTextareaField(key)) {
                  return (
                    <Field key={key} label={fieldLabel(key)}>
                      <input
                        type="text"
                        className={fieldClass}
                        value={value || ""}
                        disabled={!canEdit}
                        onChange={(e) => setFieldValue(key, e.target.value)}
                      />
                    </Field>
                  );
                }
                return (
                  <Field key={key} label={fieldLabel(key)} span="sm:col-span-2">
                    <textarea
                      className={`${textareaClass} min-h-[76px]`}
                      value={value || ""}
                      disabled={!canEdit}
                      onChange={(e) => setFieldValue(key, e.target.value)}
                    />
                  </Field>
                );
              })}
          </div>
        </Section>

        {isReviewer && !isDraft ? (
          <OfficeReviewSection
            value={reviewNotes}
            onChange={setReviewNotes}
          />
        ) : null}
      </div>

      <FormActionBar
        permissions={permissions}
        status={form.status}
        canEdit={canEdit}
        saving={saving}
        onSubmit={submit}
      />
    </div>
  );
};

export default PatientClinicalFormPage;
